import React from "react";
import { createPortal } from "react-dom";
import { useExitAnim } from "../../hooks/useExitAnim";
import { formatTimeInTZ } from "../../lib/timezone";
import { DailyBreakdownRow } from "../../hooks/useSetterDailyBreakdown";
import { SessionTimelineRow } from "../../hooks/useSetterSessionTimeline";
import { SessionClockChart } from "./SessionClockChart";

interface WorkPatternModalProps {
  isOpen: boolean;
  onClose: () => void;
  setterName: string;
  dateRange: { start: Date | null; end: Date | null };
  timezone: string;
  loadingDaily: boolean;
  dailyData: DailyBreakdownRow[];
  sessionData: SessionTimelineRow[];
  allDates: string[];
  /** `workDate` is the RPC's own org-tz-bucketed `work_date` on a day-row click,
   *  and `null` on a clock-arc click. Arc mode passing null is load-bearing: it
   *  is what hides the setter switcher, because one arc is one of this setter's
   *  N sessions and no other setter has an equivalent to switch to. */
  onSessionClick: (
    start: string,
    end: string,
    label: string,
    sessionCount: number,
    workDate: string | null
  ) => void;
}

function formatHours(mins: number): string {
  const hrs = mins / 60;
  return hrs >= 10 ? `${Math.round(hrs)}h` : `${hrs.toFixed(1)}h`;
}

function formatDateLabel(dateStr: string): string {
  // Use UTC noon to avoid date-shift from browser-local midnight interpretation.
  // work_date is already bucketed in org tz in the DB — display as UTC so the date stays stable.
  const d = new Date(dateStr + "T12:00:00Z");
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    weekday: "short",
    timeZone: "UTC",
  });
}

export const WorkPatternModal: React.FC<WorkPatternModalProps> = ({
  isOpen,
  onClose,
  setterName,
  dateRange,
  timezone,
  loadingDaily,
  dailyData,
  sessionData,
  allDates,
  onSessionClick,
}) => {
  // Keep the modal mounted through the 200ms exit so anim-modal-out plays.
  const { mounted, closing } = useExitAnim(isOpen, 200);

  if (!mounted) return null;

  const dataByDate: Record<string, DailyBreakdownRow> = {};
  for (const row of dailyData) {
    dataByDate[row.work_date] = row;
  }

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className={`${closing ? "anim-backdrop-out" : "anim-backdrop-in"} absolute inset-0 bg-black/40 backdrop-blur-sm`}
        onClick={onClose}
      />
      {/* anim-modal-in on the NON-glass wrapper (glass panel can't take transforms) */}
      <div className={`${closing ? "anim-modal-out" : "anim-modal-in"} w-full max-w-5xl`}>
        <div className="relative detail-overlay rounded-xl shadow-2xl w-full max-h-[85vh] overflow-hidden">
          <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.08]">
            <div>
              <h2 className="token-18 text-glass">{setterName} — Daily Work Pattern</h2>
              <p className="token-14 text-glass-muted mt-1">
                {dateRange.start?.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  timeZone: timezone,
                })}
                {" – "}
                {dateRange.end?.toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  timeZone: timezone,
                })}
              </p>
            </div>
            <button
              onClick={onClose}
              className="anim-tap text-glass-muted hover:text-glass-secondary transition-colors"
              aria-label="Close modal"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
          <div className="p-6 overflow-y-auto max-h-[calc(85vh-80px)]">
            {loadingDaily ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : (
              <div className="flex flex-col lg:flex-row gap-6">
                <div className="flex-shrink-0 self-center lg:self-start">
                  <SessionClockChart
                    sessions={sessionData}
                    dates={allDates}
                    timezone={timezone}
                    size={280}
                    onSessionClick={(sessionStart, sessionEnd, label) => {
                      // One arc is one session — never the day total.
                      // workDate is null: an arc is a narrow sub-window, so the
                      // switcher stays hidden and the window is never widened.
                      onSessionClick(sessionStart, sessionEnd, label, 1, null);
                    }}
                  />
                </div>
                <div className="flex-1 overflow-x-auto min-w-0">
                  <table className="token-14 w-full">
                    <thead>
                      <tr className="border-b border-black/[0.08]">
                        <th className="text-left py-3 pr-4 font-semibold text-glass-secondary">
                          Date
                        </th>
                        <th className="text-left py-3 pr-4 font-semibold text-glass-secondary">
                          First Msg
                        </th>
                        <th className="text-left py-3 pr-4 font-semibold text-glass-secondary">
                          Last Msg
                        </th>
                        <th className="text-left py-3 pr-4 font-semibold text-glass-secondary">
                          Hours
                        </th>
                        <th className="text-left py-3 pr-4 font-semibold text-glass-secondary">
                          Sessions
                        </th>
                        <th className="text-left py-3 font-semibold text-glass-secondary">Break</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allDates.map((date, idx) => {
                        const row = dataByDate[date];
                        if (!row) {
                          return (
                            <tr
                              key={date}
                              className="border-b border-black/[0.08] anim-pop"
                              style={{
                                animationDelay: `${Math.min(idx, 15) * 100}ms`,
                                animationDuration: "480ms",
                              }}
                            >
                              <td className="py-3 pr-4 text-glass-muted italic whitespace-nowrap">
                                {formatDateLabel(date)}
                              </td>
                              <td colSpan={5} className="py-3 text-glass-muted italic">
                                — no activity —
                              </td>
                            </tr>
                          );
                        }
                        const breakHigh = row.break_minutes > 300;
                        const workLow = row.work_minutes < 120;
                        return (
                          <tr
                            key={date}
                            className="border-b border-black/[0.08] hover:bg-black/[0.03] cursor-pointer transition-colors anim-pop"
                            style={{
                              animationDelay: `${Math.min(idx, 15) * 100}ms`,
                              animationDuration: "480ms",
                            }}
                            onClick={() =>
                              onSessionClick(
                                row.first_msg_time,
                                row.last_msg_time,
                                formatDateLabel(date),
                                row.session_count,
                                // row.work_date, NOT the `date` loop key. `date`
                                // comes from allDates, built with browser-local
                                // getters, so it drifts off the RPC's org-tz
                                // bucket for any non-org-tz viewer.
                                row.work_date
                              )
                            }
                            title="Click to view messages"
                          >
                            <td className="py-3 pr-4 text-glass-secondary whitespace-nowrap">
                              {formatDateLabel(date)}
                            </td>
                            <td className="py-3 pr-4 text-glass-secondary whitespace-nowrap">
                              {formatTimeInTZ(row.first_msg_time, timezone)}
                            </td>
                            <td className="py-3 pr-4 text-glass-secondary whitespace-nowrap">
                              {formatTimeInTZ(row.last_msg_time, timezone)}
                            </td>
                            <td
                              className={`py-3 pr-4 whitespace-nowrap ${workLow ? "text-ds-amber font-medium" : "text-glass-secondary"}`}
                            >
                              {formatHours(row.work_minutes)}
                            </td>
                            <td className="py-3 pr-4 text-glass-secondary whitespace-nowrap">
                              {row.session_count}
                            </td>
                            <td
                              className={`py-3 whitespace-nowrap ${breakHigh ? "text-ds-red font-medium" : "text-glass-secondary"}`}
                            >
                              {formatHours(row.break_minutes)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
